"use client";

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Types for the registry state
interface DashRegistryState {
  loadedComponents: Set<string>;
  loadingComponents: Set<string>;
  failedComponents: Set<string>;
  componentRegistry: Map<string, React.ComponentType<any>>;
  isLoading: boolean;
}

interface DashRegistryContextType {
  state: DashRegistryState;
  requestComponents: (componentNames: string[]) => Promise<void>;
  getComponentRegistry: (componentNames: string[]) => Record<string, React.ComponentType<any>>;
  isComponentReady: (componentName: string) => boolean;
  areComponentsReady: (componentNames: string[]) => boolean;
}

// Create context
const DashRegistryContext = createContext<DashRegistryContextType | null>(null);

interface DashReactRegistryProviderProps {
  children: ReactNode;
}

export function DashReactRegistryProvider({ children }: DashReactRegistryProviderProps) {
  const [state, setState] = useState<DashRegistryState>(() => ({
    loadedComponents: new Set<string>(),
    loadingComponents: new Set<string>(),
    failedComponents: new Set<string>(),
    componentRegistry: new Map<string, React.ComponentType<any>>(),
    isLoading: false
  }));

  // Request components to be loaded
  const requestComponents = useCallback(async (componentNames: string[]): Promise<void> => {
    if (componentNames.length === 0) {
      console.log('📦 No components requested for dashboard registry');
      return;
    }

    const uniqueComponents = [...new Set(componentNames)];

    // Check what we actually need to load
    const componentsToLoad = uniqueComponents.filter(name => 
      !state.loadedComponents.has(name) && 
      !state.loadingComponents.has(name) &&
      !state.failedComponents.has(name)
    );

    if (componentsToLoad.length === 0) {
      return; // All components already available
    }

    // Mark components as loading
    setState(prevState => ({
      ...prevState,
      loadingComponents: new Set([...prevState.loadingComponents, ...componentsToLoad]),
      isLoading: true
    }));

    try {
      // Use dashboard component registry to load components
      const { dashboardComponentRegistry } = await import('@/lib/client/dashboard-component-registry');
      const componentRegistry = await dashboardComponentRegistry.createExecutionContext(componentsToLoad);

      // Update state with loaded components
      setState(prevState => {
        const newLoadedComponents = new Set([...prevState.loadedComponents, ...componentsToLoad]);
        const newLoadingComponents = new Set(prevState.loadingComponents);
        const newComponentRegistry = new Map(prevState.componentRegistry);

        // Store ALL components from execution context (no filtering)
        Object.entries(componentRegistry).forEach(([name, component]) => {
          newComponentRegistry.set(name, component);
        });

        // Remove requested components from loading state
        componentsToLoad.forEach(componentName => {
          newLoadingComponents.delete(componentName);
        });

        return {
          ...prevState,
          loadedComponents: newLoadedComponents,
          loadingComponents: newLoadingComponents,
          componentRegistry: newComponentRegistry,
          isLoading: newLoadingComponents.size > 0
        };
      });


    } catch (error) {
      console.error('❌ Dash registry: Failed to load components:', error);

      // Mark failed components
      setState(prevState => {
        const newLoadingComponents = new Set(prevState.loadingComponents);
        const newFailedComponents = new Set([...prevState.failedComponents, ...componentsToLoad]);

        // Remove from loading, add to failed
        componentsToLoad.forEach(componentName => {
          newLoadingComponents.delete(componentName);
        });

        return {
          ...prevState,
          loadingComponents: newLoadingComponents,
          failedComponents: newFailedComponents,
          isLoading: newLoadingComponents.size > 0
        };
      });

      throw error;
    }
  }, [state.loadedComponents, state.loadingComponents, state.failedComponents]);

  // Get component registry - return all available components (no filtering)
  const getComponentRegistry = useCallback((componentNames: string[]): Record<string, React.ComponentType<any>> => {
    const registry: Record<string, React.ComponentType<any>> = {};
    
    // Convert Map to Record - provide all loaded components
    state.componentRegistry.forEach((component, name) => {
      registry[name] = component;
    });

    return registry;
  }, [state.componentRegistry]);

  // Check if a single component is ready
  const isComponentReady = useCallback((componentName: string): boolean => {
    return state.loadedComponents.has(componentName);
  }, [state.loadedComponents]);

  // Check if all components in array are ready
  const areComponentsReady = useCallback((componentNames: string[]): boolean => {
    if (componentNames.length === 0) return true;
    
    return componentNames.every(name => state.loadedComponents.has(name));
  }, [state.loadedComponents]);

  const contextValue: DashRegistryContextType = {
    state,
    requestComponents,
    getComponentRegistry,
    isComponentReady,
    areComponentsReady
  };

  return (
    <DashRegistryContext.Provider value={contextValue}>
      {children}
    </DashRegistryContext.Provider>
  );
}

// Custom hook to use the registry context
export function useDashRegistryContext() {
  const context = useContext(DashRegistryContext);
  
  if (!context) {
    throw new Error('useDashRegistryContext must be used within a DashReactRegistryProvider');
  }
  
  return context;
}